import { useEffect, useMemo, useState } from 'react'
import { supabase } from './supabaseClient'
import SchoolDetail from './SchoolDetail'
import './Coaches.css'

const DIVISIONS = ['All', 'D1', 'D2', 'D3', 'JUCO']

const COACH_TITLES = [
  'Head Coach',
  'Associate Head Coach',
  'Assistant Coach',
  'Pitching Coach',
  'Hitting Coach',
  'Defensive Coach',
  'Bullpen Coach',
  'Volunteer Assistant',
  'Recruiting Coordinator',
  'Director of Player Personnel',
  'Director of Operations',
  'Director of Player Development',
  'Strength & Conditioning Coach',
  'Athletic Trainer',
  'Graduate Assistant',
]

const DIV_BADGE = {
  D1:   'coaches-badge-d1',
  D2:   'coaches-badge-d2',
  D3:   'coaches-badge-d3',
  JUCO: 'coaches-badge-juco',
}

export default function Coaches({ user }) {
  useEffect(() => { document.title = 'Coaches Database — CommitHQ' }, [])

  const [coaches, setCoaches]       = useState([])
  const [loading, setLoading]       = useState(true)
  const [divMap, setDivMap]         = useState({}) // school_name.lower → division
  const [pipelineMap, setPipelineMap] = useState({}) // school_name.lower → full pipeline row
  const [detailSchool, setDetailSchool] = useState(null)
  const [addingSchool, setAddingSchool] = useState(null)

  // Filters
  const [query, setQuery]           = useState('')
  const [divFilter, setDivFilter]   = useState('All')
  const [titleFilter, setTitleFilter] = useState('All')

  // Sort
  const [sortKey, setSortKey]       = useState('school_name')
  const [sortDir, setSortDir]       = useState('asc')

  // ── Load data ─────────────────────────────────────────────────────

  useEffect(() => {
    async function load() {
      const [coachesRes, schoolsRes, pipelineRes] = await Promise.all([
        supabase.from('coaches_db').select('*').limit(10000),
        supabase.from('schools_db').select('school_name, division'),
        supabase.from('pipeline_schools').select('*').eq('user_id', user.id),
      ])

      setCoaches(coachesRes.data || [])

      const dm = {}
      for (const s of (schoolsRes.data || [])) {
        dm[s.school_name.toLowerCase()] = s.division
      }
      setDivMap(dm)

      const pm = {}
      for (const row of (pipelineRes.data || [])) {
        pm[row.school_name.toLowerCase()] = row
      }
      setPipelineMap(pm)
      setLoading(false)
    }
    load()
  }, [user.id])

  // ── Filtered + sorted list ────────────────────────────────────────

  const filtered = useMemo(() => {
    let list = coaches

    if (divFilter !== 'All') {
      list = list.filter(c => {
        const div = c.division || divMap[(c.school_name || '').toLowerCase()] || ''
        return div === divFilter
      })
    }

    if (titleFilter !== 'All') {
      list = list.filter(c => c.title === titleFilter)
    }

    if (query.trim()) {
      const q = query.trim().toLowerCase()
      list = list.filter(c =>
        `${c.first_name ?? ''} ${c.last_name ?? ''}`.toLowerCase().includes(q) ||
        (c.school_name || '').toLowerCase().includes(q)
      )
    }

    return [...list].sort((a, b) => {
      let va, vb
      if (sortKey === 'name') {
        va = `${a.last_name ?? ''} ${a.first_name ?? ''}`.toLowerCase()
        vb = `${b.last_name ?? ''} ${b.first_name ?? ''}`.toLowerCase()
      } else if (sortKey === 'division') {
        va = (a.division || divMap[(a.school_name || '').toLowerCase()] || '').toLowerCase()
        vb = (b.division || divMap[(b.school_name || '').toLowerCase()] || '').toLowerCase()
      } else {
        va = (a[sortKey] ?? '').toLowerCase()
        vb = (b[sortKey] ?? '').toLowerCase()
      }
      const cmp = va.localeCompare(vb)
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [coaches, divFilter, titleFilter, query, sortKey, sortDir, divMap])

  // ── Handlers ─────────────────────────────────────────────────────

  function handleSort(key) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  async function handleViewSchool(coach) {
    const key = (coach.school_name || '').toLowerCase()
    const existing = pipelineMap[key]
    if (existing) {
      setDetailSchool(existing)
      return
    }
    setAddingSchool(coach.school_name)
    const division = coach.division || divMap[key] || null
    const { data, error } = await supabase
      .from('pipeline_schools')
      .insert({
        user_id:     user.id,
        school_name: coach.school_name,
        division,
        sport:       'Baseball',
        status:      'Researching',
      })
      .select()
      .single()
    if (!error && data) {
      setPipelineMap(prev => ({ ...prev, [key]: data }))
      setDetailSchool(data)
    }
    setAddingSchool(null)
  }

  // ── Derived (must be before any early return) ────────────────────

  const schoolCount = useMemo(
    () => new Set(coaches.map(c => c.school_name).filter(Boolean)).size,
    [coaches]
  )

  // ── SchoolDetail drill-down ───────────────────────────────────────

  if (detailSchool) {
    return (
      <SchoolDetail
        pipelineSchool={detailSchool}
        user={user}
        onBack={() => setDetailSchool(null)}
      />
    )
  }

  // ── Render ────────────────────────────────────────────────────────

  return (
    <div className="coaches-page">

      {/* Header */}
      <div className="coaches-header">
        <h1 className="coaches-title">Coaches Database</h1>
        <p className="coaches-subtitle">
          {loading
            ? 'Loading…'
            : `${coaches.length.toLocaleString()} coaches across ${schoolCount.toLocaleString()} programs`}
        </p>
      </div>

      {/* Controls */}
      <div className="coaches-controls">
        <div className="coaches-controls-top">
          {/* Search */}
          <div className="coaches-search-wrap">
            <svg className="coaches-search-icon" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              className="coaches-search"
              type="text"
              placeholder="Search coaches or schools…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
            {query && (
              <button className="coaches-search-clear" onClick={() => setQuery('')} aria-label="Clear">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            )}
          </div>

          {/* Title filter */}
          <select
            className="coaches-title-select"
            value={titleFilter}
            onChange={e => setTitleFilter(e.target.value)}
          >
            <option value="All">All Titles</option>
            {COACH_TITLES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>

        {/* Division pills */}
        <div className="coaches-div-filters">
          {DIVISIONS.map(d => (
            <button
              key={d}
              className={`coaches-div-btn${divFilter === d ? ' active' : ''}${d !== 'All' ? ` cdiv-${d.toLowerCase()}` : ''}`}
              onClick={() => setDivFilter(d)}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      {/* Result count */}
      {!loading && (
        <p className="coaches-count">
          Showing <strong>{filtered.length.toLocaleString()}</strong> coaches
        </p>
      )}

      {/* Table */}
      {loading ? (
        <div className="coaches-loading">
          <span className="coaches-spinner" />
          Loading coaches…
        </div>
      ) : filtered.length === 0 ? (
        <div className="coaches-empty">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <p>No coaches match your filters.</p>
        </div>
      ) : (
        <div className="coaches-table-wrap">
          <table className="coaches-table">
            <thead>
              <tr>
                <SortTh label="Coach" sk="name"        sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Title" sk="title"       sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="School" sk="school_name" sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Div"   sk="division"    sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                <th className="coaches-th">Email</th>
                <th className="coaches-th coaches-th-actions"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(coach => {
                const division = coach.division || divMap[(coach.school_name || '').toLowerCase()] || ''
                const pipelineRow = pipelineMap[(coach.school_name || '').toLowerCase()]
                const isAdding = addingSchool === coach.school_name

                return (
                  <tr key={coach.id} className="coaches-row">
                    <td className="coaches-td cell-coach-name">
                      {coach.first_name} {coach.last_name}
                    </td>
                    <td className="coaches-td cell-title">
                      {coach.title === 'Head Coach' && (
                        <svg className="icon-hc-star" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                      )}
                      {coach.title || <span className="coaches-cell-empty">—</span>}
                    </td>
                    <td className="coaches-td cell-school">
                      {coach.school_name || <span className="coaches-cell-empty">—</span>}
                    </td>
                    <td className="coaches-td cell-div">
                      {division ? (
                        <span className={`coaches-div-badge ${DIV_BADGE[division] ?? 'coaches-badge-d3'}`}>
                          {division}
                        </span>
                      ) : (
                        <span className="coaches-cell-empty">—</span>
                      )}
                    </td>
                    <td className="coaches-td cell-email">
                      {coach.email
                        ? (
                          <span className="coaches-email-cell">
                            <a href={`mailto:${coach.email}`} className="coaches-email-link">{coach.email}</a>
                            {coach.email_verified === false && (
                              <span className="predicted-badge">Predicted</span>
                            )}
                          </span>
                        )
                        : <span className="coaches-cell-empty">—</span>}
                    </td>
                    <td className="coaches-td cell-actions">
                      {pipelineRow ? (
                        <button
                          className="btn-view-school"
                          onClick={() => setDetailSchool(pipelineRow)}
                        >
                          View School
                        </button>
                      ) : (
                        <button
                          className="btn-add-view"
                          onClick={() => handleViewSchool(coach)}
                          disabled={isAdding}
                        >
                          {isAdding ? 'Adding…' : 'Add & View'}
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Sort column header ─────────────────────────────────────────────

function SortTh({ label, sk, sortKey, sortDir, onSort }) {
  const active = sortKey === sk
  return (
    <th className={`coaches-th coaches-th-sort${active ? ' sort-active' : ''}`} onClick={() => onSort(sk)}>
      <span className="coaches-th-inner">
        {label}
        <span className="sort-arrow">
          {active
            ? sortDir === 'asc'
              ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="18 15 12 9 6 15"/></svg>
              : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
            : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ opacity: 0.3 }}><polyline points="18 15 12 9 6 15"/></svg>
          }
        </span>
      </span>
    </th>
  )
}
